import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import SocialMedia from './social-media';

const formatMarkdownText = (text) => {
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
  text = text.replace(/^- /gm, '• ');
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>');
  return text;
};

const DifferentiationPoint = ({ point }) => {
  return (
    <p 
      className="mb-2 text-white text-base leading-relaxed"
      dangerouslySetInnerHTML={{ 
        __html: formatMarkdownText(point.trim())
      }} 
    />
  );
};

const CompanyDetailsOverlay = ({ companyId, onClose, baseUrl, companyName }) => {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    let progressInterval = null;

    const fetchCompanyDetails = async () => {
      try {
        progressInterval = setInterval(() => {
          setProgress(prev => {
            if (prev >= 95) {
              clearInterval(progressInterval);
              return 95;
            }
            return prev + 5;
          });
        }, 600);

        const response = await axios.get(`${baseUrl}/get-company-details`, {
          headers: { companyId },
        });

        setProgress(100);
        setCompanyData(response.data);
        clearInterval(progressInterval);
        
        setTimeout(() => {
          setLoading(false);
        }, 500);

      } catch (err) {
        if (progressInterval) clearInterval(progressInterval);
        setError("Failed to load company details");
        setLoading(false);
      }
    };

    fetchCompanyDetails();
    return () => { if (progressInterval) clearInterval(progressInterval); };
  }, [companyId, baseUrl]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="min-h-screen py-6 px-4 md:px-6"
          onClick={e => e.stopPropagation()}
        >
          {loading ? (
            <div className="flex items-center justify-center min-h-[80vh]">
              <div className="w-full max-w-md flex flex-col items-center gap-6">
                <div className="relative h-32 w-32">
                  <svg className="absolute inset-0" viewBox="0 0 100 100">
                    <circle
                      className="stroke-gray-200"
                      strokeWidth="8"
                      fill="transparent"
                      r="44"
                      cx="50"
                      cy="50"
                    />
                    <circle
                      className="transition-all duration-300"
                      strokeWidth="8"
                      fill="transparent"
                      r="44"
                      cx="50"
                      cy="50"
                      strokeDasharray="276.46"
                      strokeDashoffset={276.46 * (1 - progress / 100)}
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                      stroke="#FC8F41"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-semibold text-white">{Math.round(progress)}%</span>
                  </div>
                </div>

                <div className="space-y-4 w-full">
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-orng rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-lg font-semibold text-white">
                      Loading {companyName} Details
                    </p>
                    <p className="text-sm text-gray-300">
                      {progress < 33 && "Fetching company information..."}
                      {progress >= 33 && progress < 66 && "Analyzing data..."}
                      {progress >= 66 && progress < 100 && "Almost there..."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center min-h-[80vh]">
              <p className="text-base text-red-500 bg-white p-4 rounded-lg shadow-lg">{error}</p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-xl">
              {/* Header */}
              <div className="relative bg-orng p-6 rounded-t-xl">
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 text-white hover:bg-white/20 p-2 rounded-full transition-colors"
                >
                  <span className="text-base">✕</span>
                </button>
                <h1 className="text-3xl font-bold text-white">
                  {companyData.company_name}
                </h1>
              </div>

              {/* Description */}
              <div className="p-6 border-b border-gray-200">
                <p className="text-base text-gray-700 leading-relaxed">
                  {companyData.company_description}
                </p>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 border-b border-gray-200">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-semibold mb-2">Company Size</h3>
                  <p className="text-lg text-orng">
                    {companyData.company_details.company_size !== 'not found' 
                      ? companyData.company_details.company_size 
                      : 'Not available'}
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-semibold mb-2">Funding Status</h3>
                  <p className="text-lg text-orng">
                    {companyData.company_details.funding_status !== 'not found' 
                      ? companyData.company_details.funding_status 
                      : 'Not available'}
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-semibold mb-2">Search Volumes</h3>
                  <p className="text-lg text-orng">
                    {companyData.company_details.search_volumes !== 'not found' 
                      ? companyData.company_details.search_volumes 
                      : 'Not available'}
                  </p>
                </div>
              </div>

              {/* Posts and Differentiation */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                {/* Social Media Posts */}
                <div className="h-[600px] bg-white rounded-lg border border-gray-200">
                  <SocialMedia posts={companyData.company_posts} />
                </div>

                {/* Differentiation and Website Link */}
                <div className="flex flex-col gap-4">
                  <div className="flex-grow bg-orng p-6 rounded-lg border border-orange-100">
                    <h3 className="text-lg font-semibold mb-4 text-white">Key Differentiation</h3>
                    <div className="space-y-2">
                      {companyData.company_details.differentiation
                        .split('\n')
                        .filter(point => point.trim())
                        .map((point, index) => (
                          <DifferentiationPoint key={index} point={point} />
                        ))}
                    </div>
                  </div>
                  
                  <a
                    href={companyData.company_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center bg-orng hover:bg-orange-600 text-white py-3 px-6 rounded-lg transition-colors font-medium text-sm"
                  >
                    Visit Website →
                  </a>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompanyDetailsOverlay;